import { inject, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { FeatureFlagTypes, TimeClockEventType } from '@raptor-fe/core';
import { firstValueFrom } from 'rxjs';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { ToastService } from '../toast-service/toast.service';
import { TranslationService } from '../translate-service/translation.service';
import { TimeClockService } from './time-clock.service';

/**
 * `errors.reason` the API returns when it refuses a job or inspection clock-on because the technician has not
 * clocked in for the day. Matched instead of the message, which is translated and tenant-overridable.
 */
export const NOT_CLOCKED_IN_FOR_DAY_REASON = 'NotClockedInForDay';

/**
 * Result of the day clock-in pre-flight: `proceed` needed nothing, `clocked-in` opened the day first, and
 * `cancelled` means the technician declined or the day clock-in failed, so the clock-on must not be attempted.
 */
export type DayClockInOutcome = 'proceed' | 'clocked-in' | 'cancelled';

/**
 * True when an HTTP error is the API refusing a clock-on because the technician has not clocked in for the day.
 */
export function isNotClockedInForDayError(error: unknown): boolean {
    return (error as { error?: { errors?: { reason?: unknown } } } | null)?.error?.errors?.reason === NOT_CLOCKED_IN_FOR_DAY_REASON;
}

/**
 * The one confirm-then-clock-in step every job and inspection start path shares, so tenants with
 * {@link FeatureFlagTypes.DailyClockIn} get the same prompt wherever work is started. The API enforces the same
 * rule, which is why callers also route an {@link isNotClockedInForDayError} rejection through
 * {@link DayClockInGuardService.promptAndClockIn}: the client's shift state can be stale.
 */
@Injectable({
    providedIn: 'root',
})
export class DayClockInGuardService {
    private readonly appContext = inject(ApplicationContextStore);
    private readonly timeClockService = inject(TimeClockService);
    private readonly alertCtrl = inject(AlertController);
    private readonly toastService = inject(ToastService);
    private readonly translationService = inject(TranslationService);

    /**
     * Pre-flight for starting work. Tenants without the flag, and technicians already on shift, proceed untouched.
     */
    async ensureClockedInForDay(onShift: boolean): Promise<DayClockInOutcome> {
        if (onShift || !this.appContext.features().includes(FeatureFlagTypes.DailyClockIn)) {
            return 'proceed';
        }

        return this.promptAndClockIn();
    }

    /**
     * Asks the technician to clock in for the day and, on Yes, opens the WorkDay clock. A failed clock-in is
     * reported and resolves `cancelled` so the caller never starts work the API would refuse.
     */
    async promptAndClockIn(): Promise<Exclude<DayClockInOutcome, 'proceed'>> {
        if (!(await this.confirm())) {
            return 'cancelled';
        }

        try {
            await firstValueFrom(this.timeClockService.clockIn(this.appContext.tenantId(), this.appContext.branch().id ?? '', TimeClockEventType.WorkDay));
        } catch (error) {
            await this.toastService.showError(
                (error as { error?: { message?: string } } | null)?.error?.message ??
                    this.translationService.getTranslation('Time Clock', 'Day Clock In', 'Failed to clock in for the day.'),
            );
            return 'cancelled';
        }

        this.appContext.fetchWorkDayClockStatus();
        return 'clocked-in';
    }

    private async confirm(): Promise<boolean> {
        const alert = await this.alertCtrl.create({
            header: this.translationService.getTranslation('Time Clock', 'Day Clock In', 'Not clocked in'),
            message: this.translationService.getTranslation('Time Clock', 'Day Clock In', "You're not clocked in for the day. Clock in now?"),
            buttons: [
                { text: this.translationService.getTranslation('Common', 'Button', 'No'), role: 'cancel' },
                { text: this.translationService.getTranslation('Common', 'Button', 'Yes'), role: 'confirm' },
            ],
        });
        await alert.present();

        const { role } = await alert.onDidDismiss();
        return role === 'confirm';
    }
}
